import React, { useState } from 'react';
import { useUser } from '../contexts/UserContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { PLANS, PRO_MONTHLY, BIZ_MONTHLY } from '../config/pricing';
import './PricingSection.css';

const PricingSection = () => {
  const { user, upgradeToPlan } = useUser();
  const { formatPrice } = useCurrency();
  const [billingCycle, setBillingCycle] = useState('monthly');

  const plans = {
    starter: {
      name: "Free Tier",
      description: "Try VlogClip AI free — no credit card required",
      monthly: { price: 0, clips: 15, videos: 5 },
      yearly: { price: 0, clips: 15, videos: 5, savings: "Always Free" },
      features: [
        "5 videos per month",
        "3 clips per video",
        "2 aspect ratios (9:16, 1:1)",
        "AI hotspot detection",
        "TikTok-style AI subtitles on clips",
        "720p portrait output (watermarked)",
        "Fast AI-powered processing",
        "Community support"
      ],
      color: "from-blue-500 to-purple-600",
      popular: false
    },
    pro: {
      name: "Creator Pro",
      description: "For serious creators who want viral content daily",
      monthly: { price: PRO_MONTHLY, clips: PLANS.pro.clips, videos: PLANS.pro.videos },
      yearly: { price: PLANS.pro.price.yearly, clips: PLANS.pro.clips, videos: PLANS.pro.videos, savings: "20%" },
      features: [
        "20 videos per month",
        "5 clips per video",
        "1080p HD portrait output (no watermark)",
        "AI Hook overlay (viral attention grabber)",
        "Caption translation (31 languages)",
        "Advanced AI hotspot detection",
        "TikTok-style AI subtitles on every clip",
        "Upload private videos, create viral social clips (max 2GB/video)",
        "Batch processing (3 videos at once)",
        "Priority processing",
        "Email support",
        "Instant downloads",
        "Share to social platforms"
      ],
      color: "from-orange-500 to-red-600",
      popular: false,
      badge: null
    },
    agency: {
      name: "Business",
      description: "For agencies and businesses managing multiple creators",
      monthly: { price: BIZ_MONTHLY, clips: PLANS.business.clips, videos: PLANS.business.videos },
      yearly: { price: PLANS.business.price.yearly, clips: PLANS.business.clips, videos: PLANS.business.videos, savings: "20%" },
      features: [
        "60 videos per month",
        "8 clips per video",
        "1080p HD portrait output (no watermark)",
        "Everything in Creator Pro",
        "AI Hook overlay (viral attention grabber)",
        "Caption translation (31 languages)",
        "TikTok-style AI subtitles on every clip",
        "Upload private videos, create viral social clips (max 5GB/video)",
        "Commercial usage rights",
        "Batch processing (4 videos at once) — priority queue",
        "Custom logo & watermark branding",
        "Priority support",
        "Usage analytics",
        "API access (coming soon)",
        "Share to social platforms"
      ],
      color: "from-purple-600 to-pink-600",
      popular: false,
      badge: null
    }
  };

  const [userReviews, setUserReviews] = React.useState([]);

  React.useEffect(() => {
    fetch('/api/reviews/approved')
      .then(res => res.json())
      .then(data => { if (Array.isArray(data)) setUserReviews(data); })
      .catch(() => {});
  }, []);

  const getPrice = (plan) => {
    return billingCycle === 'yearly' ? plan.yearly.price : plan.monthly.price;
  };

  const calculateSavings = (plan) => {
    if (billingCycle === 'yearly' && plan.yearly.savings) {
      const monthlyCost = plan.monthly.price * 12;
      const yearlyCost = plan.yearly.price * 12;
      const saved = monthlyCost - yearlyCost;
      return saved;
    }
    return 0;
  };

  const handlePlanSelection = async (planKey) => {
    if (!user.isAuthenticated) {
      window.dispatchEvent(new CustomEvent('open-auth-modal', { detail: { mode: 'register' } }));
      return;
    }
    if (planKey === 'starter') {
      if (user.plan === 'free' || user.plan === 'starter' || !user.plan) {
        alert('You\'re already on the Free plan!');
      }
      return;
    }
    const planType = planKey === 'agency' ? 'business' : planKey;
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/payments/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': 'Bearer ' + token } : {})
        },
        body: JSON.stringify({ planType, billingPeriod: 'monthly' })
      });
      const data = await response.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        alert('Unable to start checkout. Please try again or contact support.');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Unable to start checkout. Please try again.');
    }
  };

  return (
    <div className="pricing-section">
      <div className="pricing-header">
        <h2 className="pricing-title">
          Pricing
        </h2>
        <p className="pricing-subtitle">
          Simple per-video pricing. No credit systems, no surprise overages.
        </p>

        <div className="billing-toggle">
          <span className={billingCycle === 'monthly' ? 'active' : ''}>Monthly</span>
          <button
            className="toggle-switch"
            onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
          >
            <div className={`toggle-slider ${billingCycle === 'yearly' ? 'yearly' : 'monthly'}`}></div>
          </button>
          <span className={billingCycle === 'yearly' ? 'active' : ''}>
            Yearly <span className="savings-badge">Save 21%</span>
          </span>
        </div>
      </div>

      <div className="pricing-grid">
        {Object.entries(plans).map(([key, plan]) => {
          const savings = calculateSavings(plan);
          return (
            <div key={key} className={`pricing-card ${plan.popular ? 'popular' : ''}`}>
              {plan.badge && (
                <div className="plan-badge">{plan.badge}</div>
              )}
              <div className="plan-header">
                <h3 className="plan-name">{plan.name}</h3>
                <p className="plan-description">{plan.description}</p>
              </div>
              <div className="plan-pricing">
                <div className="price-container">
                  <span className="price">{formatPrice(getPrice(plan))}</span>
                  <span className="period">/{billingCycle === 'monthly' ? 'mo' : 'mo'}</span>
                </div>
                {billingCycle === 'yearly' && savings > 0 && (
                  <div className="savings-info">
                    <span className="savings-text">Save {formatPrice(savings)}/year</span>
                    <span className="billed-text" style={{fontSize:'13px',fontWeight:'700',color:'#1e293b',marginTop:'2px'}}>{formatPrice(getPrice(plan) * 12)}/year total</span>
                    <span className="billed-text">Billed annually</span>
                  </div>
                )}
                {billingCycle === 'monthly' && (
                  <div className="billed-text">Billed monthly</div>
                )}
              </div>
              <div className="plan-stats">
                <div className="stat-item">
                  <span className="stat-number">{plan[billingCycle].clips}</span>
                  <span className="stat-label">Viral Clips/Month</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{plan[billingCycle].videos}</span>
                  <span className="stat-label">Video Uploads/Month</span>
                </div>
              </div>
              <ul className="plan-features">
                {plan.features.map((feature, index) => (
                  <li key={index} className="feature-item">
                    <span className="feature-check">✓</span>
                    <span className="feature-text">{feature}</span>
                  </li>
                ))}
              </ul>
              <button
                className={`plan-button ${plan.popular ? 'popular-button' : ''} ${(user.plan === key || (key === 'agency' && user.plan === 'business')) ? 'current-plan' : ''}`}
                onClick={() => handlePlanSelection(key)}
                disabled={user.plan === key || (key === 'agency' && user.plan === 'business')}
              >
                <span className="button-text">
                  {(user.plan === key || (key === 'agency' && user.plan === 'business')) ? 'Current Plan' :
                   key === 'starter' ? 'Start Free' : 'Get Started'}
                </span>
              </button>
            </div>
          );
        })}
      </div>

      {userReviews.length >= 3 && (
        <div className="social-proof">
          <h3 id="section-reviews" style={{fontSize:"2rem",fontWeight:"800",color:"white",textAlign:"center",marginBottom:"2rem"}}>What Our Users Say</h3>
          <div className="testimonials">
            {userReviews.slice(0, 3).map((review, idx) => (
              <div key={idx} className="testimonial">
                <div style={{marginBottom: '8px'}}>
                  {'⭐'.repeat(review.rating)}
                </div>
                <p>"{review.text}"</p>
                <span className="testimonial-author">- {review.name}, {review.role}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="pricing-faq">
        <h3 id="section-faq-home">Frequently Asked Questions</h3>
        <div className="faq-grid">
          <div className="faq-item">
            <h4>How does AI hotspot detection work?</h4>
            <p>Our AI analyzes video duration, transcript content, and identifies opening hooks, mid-video peaks, and climactic moments to automatically create 3 engaging clips from your YouTube videos.</p>
          </div>
          <div className="faq-item">
            <h4>What's included in the free tier?</h4>
            <p>The free tier lets you generate 3 clips per video from 5 videos per month (15 clips total). No credit card required. Upgrade to Pro for 5 clips per video, or Business for 8.</p>
          </div>
          <div className="faq-item">
            <h4>What video platforms do you support?</h4>
            <p>We support YouTube video input and generate captions optimized for TikTok, Instagram, Twitter, and LinkedIn. Currently working on expanding to more input sources.</p>
          </div>
          <div className="faq-item">
            <h4>Why no "minutes" or "credits" system?</h4>
            <p>Competitors charge by processing minutes — so a 1-hour video costs 60 credits. One long podcast can wipe out your monthly allowance. We think that's confusing and unfair. VlogClipAI charges per clip, so you always know what you're getting.</p>
          </div>
          <div className="faq-item">
            <h4>Do I keep my clips after downloading?</h4>
            <p>Yes! Once you download clips, they're yours to keep. We recommend downloading immediately as server storage is temporary.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingSection;
